import { Injectable } from '@angular/core';
import {
  CutPanelInspectionDataProvider,
  CustomLoggerService,
  InputAppDataService,
  AppNotificationUtilClass,
} from '@monorepo/services';
import { CpiHelper } from '@monorepo/utils';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import {
  IRejectList,
  TCpiFilter,
  TCutPanelInspectionDB,
  TCutPanelInspectionRequestItem,
  TREQUEST_ComponentTagsListByBundleTag,
  TREQUEST_CutPanelInspectionAllTransactions,
  TREQUEST_ComponentTagDetailsWithWipInfo,
  TREQUEST_GET_COMPONENT_TAG_BY_RFID,
  TREQUEST_ComponentTagsListByMasterBundleTagCPM,
  TRESPONSE_ComponentTagsListByBundleTag,
  TRESPONSE_ComponentTagsListByMasterBundleTagCPM,
  TCpiDimensionInspectionAndRemark,
  TCpiDimension,
} from '@monorepo/types';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { map, switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import {
  FetchInspectionCounts,
  InitializeCutPanelInspectionDB,
  SetSelectedMasterCutDisplayTag,
  UpdateRecentRecutsList,
} from './cut-panel-inspection.actions';
import { lastValueFrom, Subject } from 'rxjs';
import { getAllRecentRecuts, getInspectionCounts, getSelectedMasterCutDisplayTag } from './cut-panel-inspection.query';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CutPanelInspectionDBFacade {
  factoryId: string = '';
  private initCompleted = new Subject<void>();

  constructor(
    private store: Store<TCutPanelInspectionDB>,
    private apiHelper: AppNotificationUtilClass,
    private cpiDataProvider: CutPanelInspectionDataProvider,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private appMasterDBFacade: AppMasterDBFacade,
    private customLoggerService: CustomLoggerService,
    private inputAppDataService: InputAppDataService,
    private translate: TranslateService,
  ) {
    this.inputAppClientDBFacade.getSubjectKey().subscribe((res) => {
      this.factoryId = res;
    });
  }

  initializeCutPanelInspectionDB() {
    this.store.dispatch(new InitializeCutPanelInspectionDB());
  }

  cutPanelInspectionDBInitCompleted() {
    this.initCompleted.next();
  }

  getCutPanelInspectionDBInitCompletionStatus() {
    return this.initCompleted.asObservable();
  }

  fetchTodaysInspectionCounts() {
    this.store.dispatch(new FetchInspectionCounts([CpiHelper.getTodaysDateFilter()]));
  }

  getInspectionCounts() {
    return this.store.pipe(select(getInspectionCounts));
  }

  getRecentRecutIssuesList() {
    return this.store.pipe(select(getAllRecentRecuts));
  }

  updateRecentRecutList(list: IRejectList[]) {
    this.store.dispatch(new UpdateRecentRecutsList(list));
  }

  setSelectedMasterCutDisplayTag(cutDisplayTag: TCutPanelInspectionDB['selectedMasterCutDisplayTag']) {
    this.store.dispatch(new SetSelectedMasterCutDisplayTag(cutDisplayTag));
  }

  getSelectedMasterCutDisplayTag() {
    return this.store.pipe(select(getSelectedMasterCutDisplayTag));
  }

  async fetchCutPanelInspectionInfo(filters: TCpiFilter[]) {
    const loader = await this.apiHelper.presentLoading(this.translate.instant('cpi.db.loading'));
    return lastValueFrom(this.getAppDetailsAndCallFetchCpiInfoApi(filters))
      .catch((err) => {
        console.log('[FETCH_CUT_PANEL_INSPECTION_INFO_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in fetching cut panel inspection info`, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getAppDetailsAndCallFetchCpiInfoApi(filters: TCpiFilter[]) {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      switchMap((appDetails) => {
        return this.cpiDataProvider.fetchCutPanelInspectionInfo({ appDetails, filters });
      }),
    );
  }

  getComponentBundleTagDetailsWithWip(displayTag: string) {
    return this.appMasterDBFacade.getAppDetailsWithAdditionalInfo().pipe(
      switchMap((appDetails) => {
        const payload: TREQUEST_ComponentTagDetailsWithWipInfo = {
          subjectKey: this.factoryId,
          componentDisplayTagId: displayTag,
          domRole: appDetails.dnrk,
          subject: appDetails.subject,
        };
        return this.cpiDataProvider.fetchComponentTagDetailsWithWipInfo(payload);
      }),
    );
  }

  async getComponentBundleTagDetailsWithWipInfo(displayTag: string) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.fetching_available_wip'),
    );
    return lastValueFrom(this.getComponentBundleTagDetailsWithWip(displayTag))
      .catch((err) => {
        console.log('[GET_COMPONENT_BUNDLE_TAG_DETAILS_WITH_WIP_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        this.customLoggerService.log(
          `[WHAT'S_WRONG]: Error in fetching component bundle tag details with wip info: ${displayTag}`,
          stringyErr,
        );
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  async getComponentBundleTagDetails(displayTag: string) {
    const loader = await this.apiHelper.presentLoading(this.translate.instant('cpi.db.getting_tag_details'),);
    return lastValueFrom(this.cpiDataProvider.fetchComponentTagDetails({ subject_key: this.factoryId, displayTag }))
      .catch((err) => {
        console.log('[GET_COMPONENT_BUNDLE_TAG_DETAILS_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        this.customLoggerService.log(
          `[WHAT'S_WRONG]: Error in fetching component bundle tag details tag: ${displayTag}`,
          stringyErr,
        );
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  async getComponentsDetailsByMasterBundleTagId(
    cutDisplayTag: string,
    isProcessMappingEnabled: boolean,
  ): Promise<TRESPONSE_ComponentTagsListByBundleTag | TRESPONSE_ComponentTagsListByMasterBundleTagCPM | void> {
    const loader = await this.apiHelper.presentLoading(this.translate.instant('cpi.db.getting_tag_details'),);
    try {
      if (isProcessMappingEnabled) {
        return await this.#fetchComponentsListByMasterTagCPM(cutDisplayTag);
      } else {
        return await this.#fetchComponentsListByMasterTag(cutDisplayTag);
      }
    } finally {
      this.apiHelper.dismissLoadingV2(loader);
    }
  }

  #fetchComponentsListByMasterTag(cutDisplayTag: string) {
    const payload: TREQUEST_ComponentTagsListByBundleTag = {
      cutDisplayTag,
      factoryId: this.factoryId,
    };
    return lastValueFrom(this.cpiDataProvider.fetchComponentsListByMasterTag(payload)).catch((err) => {
      console.log('[GET_COMPONENTS_DETAILS_BY_MASTER_BUNDLE_TAG_ERROR]', err);
      const stringyErr = JSON.stringify(err);
      this.customLoggerService.log(
        `[WHAT'S_WRONG]: Error in fetching component details by master bundle tag: ${cutDisplayTag}`,
        stringyErr,
      );
      this.apiHelper.showPopup(
        this.translate.instant('cpi.db.error'),
        this.translate.instant('cpi.modal.something_went_wrong'),
      );
    });
  }

  async #fetchComponentsListByMasterTagCPM(cutDisplayTag: string) {
    const appDetails = await lastValueFrom(this.appMasterDBFacade.getAppDetailsCPI());
    const payload: TREQUEST_ComponentTagsListByMasterBundleTagCPM = {
      cutDisplayTag,
      factoryId: this.factoryId,
      appDetails,
    };
    return lastValueFrom(this.cpiDataProvider.fetchComponentsListByMasterTagCPM(payload)).catch((err) => {
      console.log('[GET_COMPONENTS_DETAILS_BY_MASTER_BUNDLE_TAG_CPM_ERROR]', err);
      const stringyErr = JSON.stringify(err);
      this.customLoggerService.log(
        `[WHAT'S_WRONG]: Error in fetching component details by master bundle tag (CPM): ${cutDisplayTag}`,
        stringyErr,
      );
      this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
    });
  }

  async updateCpiDetails(items: TCutPanelInspectionRequestItem[]) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.saving_tags'),
    );
    return lastValueFrom(this.getAppDetailsAndupdateCpiDetails(items, Date.now()))
      .catch((err) => {
        console.log('[UPDATE_INSPECTION_DETAILS_ERROR_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        const stringyItems = JSON.stringify(items);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in updating inspection details`, stringyItems, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getAppDetailsAndupdateCpiDetails(items: TCutPanelInspectionRequestItem[], eventTime: number) {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      switchMap((appDetails) => {
        return this.cpiDataProvider.updateCpiDetails({ appDetails, items, eventTime });
      }),
    );
  }

  async saveCpiDetails(item: TCutPanelInspectionRequestItem) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.saving_inspection_details'),
    );
    return lastValueFrom(this.getAppDetailsAndSaveCpiDetails(item, Date.now()))
      .catch((err) => {
        console.log('[SAVE_INSPECTION_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        const stringyItem = JSON.stringify(item);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in saving inspection details`, stringyItem, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getAppDetailsAndSaveCpiDetails(item: TCutPanelInspectionRequestItem, eventTime: number) {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      switchMap((appDetails) => {
        return this.cpiDataProvider.saveCpiDetails({ appDetails, item, eventTime });
      }),
    );
  }

  async completeInspection(item: TCutPanelInspectionRequestItem) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.completing_inspection'),
    );
    return lastValueFrom(this.getAppDetailsAndCompleteInspection(item, Date.now()))
      .catch((err) => {
        console.log('[COMPLETE_INSPECTION_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        const stringyItem = JSON.stringify(item);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in completing inspection`, stringyItem, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getAppDetailsAndCompleteInspection(item: TCutPanelInspectionRequestItem, eventTime: number) {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      switchMap((appDetails) => {
        return this.cpiDataProvider.completeInspection({ appDetails, item, eventTime });
      }),
    );
  }

  async fetchAllTransactionsData(filters: TCpiFilter[], pageNo: number, pageSize: number) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.loading_transactions'),
    );
    return lastValueFrom(this.getAppDetailsAndFetchAllTransactionsData(filters, pageNo, pageSize))
      .catch((err) => {
        console.log('[FETCH_ALL_TRANSACTIONS_DATA_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in fetching all transactions data`, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getAppDetailsAndFetchAllTransactionsData(filters: TCpiFilter[], pageNo: number, pageSize: number) {
    return this.appMasterDBFacade.getAppDetailsCPI().pipe(
      switchMap((appDetails) => {
        const payload: TREQUEST_CutPanelInspectionAllTransactions = {
          appDetails,
          filters,
          pageNo,
          pageSize,
        };
        return this.cpiDataProvider.fetchAllTransactionsData(payload);
      }),
    );
  }

  async getTagDetailsFromScannedRfidTag(rfidTag: string) {
    const loader = await this.apiHelper.presentLoading(
      this.translate.instant('cpi.db.getting_tag_details'),
    );
    return lastValueFrom(
      this.appMasterDBFacade.getAppDetailsCPI().pipe(
        map((appDetails) => [appDetails.factoryId] as const),
        switchMap(([factoryId]) => {
          const payload: TREQUEST_GET_COMPONENT_TAG_BY_RFID = {
            subjectKey: factoryId,
            rfidTag: rfidTag,
          };
          return this.inputAppDataService.getComponentTagByRfid(payload);
        }),
      ),
    )
      .catch((err) => {
        console.log('[GET_TAG_DETAILS_FROM_SCANNED_RFID_TAG_ERROR]', err);
        const stringyErr = JSON.stringify(err);
        this.customLoggerService.log(`[WHAT'S_WRONG]: Error in getting tag details from scanned rfid tag`, stringyErr);
        this.apiHelper.showPopup(this.translate.instant('cpi.db.error'),this.translate.instant('cpi.modal.something_went_wrong'));
      })
      .finally(() => {
        this.apiHelper.dismissLoadingV2(loader);
      });
  }

  getCpiDimensionInspectionAndRemarkEmptyData() {
    const emptyCpiData: TCpiDimensionInspectionAndRemark = {
      dimensions: [],
      extra_info: [],
      remark: ''
    };
    return emptyCpiData;
  }
}
